import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import TherigyInfoMessage from 'App/components/TherigyInfoMessage/TherigyInfoMessage';
import Table from 'App/components/Table';
import propOrDash from 'App/utils/propOrDash';
import dateOrDashUtc from 'App/utils/dateOrDashUtc';
import pathOrDash from 'App/utils/pathOrDash';

const propTypes = {
    data: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
    isLoading: PropTypes.bool.isRequired,
};

const defaultProps = {};

function ReferralMedicationTable({ data, isLoading }) {
    function getIcd10(row) {
        const icd10Details = row.medication?.icd10?.icd10Details;
        if (icd10Details?.dxCode) {
            return `${icd10Details.dxCode} ${icd10Details.dxDescShort}`;
        }
        return row.icd_10 || '--';
    }

    /**
     * Get the Rx Number from the referral medication row.
     * The rxNumber can come from two places:
     * 1. row.rx.rxNumber - when rx_number_id is saved in referral_medication table
     * 2. row.medication.rxNumbers - when rxNumbers are fetched separately (rx_number_id is null)
     * @param {Object} row - The referral medication row
     * @returns {string} The Rx Number(s) or '--' if not available
     */
    function getRxNumber(row) {
        // First check if rx is directly linked
        if (row.rx?.rxNumber) {
            return row.rx.rxNumber;
        }

        // Fall back to medication.rxNumbers (fetched when rx_number_id is not set)
        if (row.medication?.rxNumbers?.length) {
            return row.medication.rxNumbers.map((rxNum) => rxNum.rxNumber || rxNum.rx_number).join(', ');
        }

        return '--';
    }

    const columns = useMemo(
        () => [
            {
                Header: 'Medications',
                accessor: 'medication.name',
                Cell: ({ row }) => pathOrDash(['original', 'medication', 'name'], row),
                id: 'medication',
            },
            {
                Header: 'ICD-10',
                accessor: getIcd10,
                id: 'icd_10',
            },
            {
                Header: 'Rx Number',
                accessor: getRxNumber,
                id: 'rxNumber',
            },
            {
                Header: 'Rx Status',
                accessor: propOrDash('status'),
                id: 'status',
            },
            {
                Header: 'Rx Status Date',
                accessor: dateOrDashUtc(['statusDate']),
                id: 'statusDate',
            },
        ],
        []
    );

    return (
        <div>
            {data?.length > 0 && !isLoading ? (
                <Table
                    enablePagination={false}
                    columns={columns}
                    data={data}
                    isLoading={isLoading}
                    placeholderText="No Medication"
                />
            ) : (
                <TherigyInfoMessage message="There are currently no medications attached to this referral." />
            )}
        </div>
    );
}

ReferralMedicationTable.propTypes = propTypes;
ReferralMedicationTable.defaultProps = defaultProps;

export default ReferralMedicationTable;
